import React, { Fragment, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CompletionQuestionsModal, DocumentViewer, LoadingSpinner, UnsupportedModuleViewer, VideoPlayer, useAuth } from '../components'
import { Button, Card } from '../components/ui'
import { API_BASE_URL, LoadingSpinnerMeta, ModuleCompletionModalMeta, TrainingModulePageMeta } from '../data'
import { TrainingModulePageMetaProps, TrainingModule as TrainingModuleType } from '../data/types'
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, ConstructionIcon, ExternalLinkIcon, XIcon } from '../icons'
import TrainingScormPlayer from './TrainingScormPlayer'


type Props = {
    meta: TrainingModulePageMetaProps
    adminMode?: boolean
}

export const TrainingModule = (props: Props) => {
    const { meta, adminMode = false } = props
    const { trainingId, moduleIndex } = useParams<{ trainingId: string; moduleIndex: string }>()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { user, refreshIncompletePolicies } = useAuth()

    const [loading, setLoading] = useState(true)
    const [togglingCompletion, setTogglingCompletion] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [trainingName, setTrainingName] = useState<string>('')
    const [isPolicy, setIsPolicy] = useState<boolean>(false)
    const [module, setModule] = useState<TrainingModuleType | null>(null)
    const [totalModules, setTotalModules] = useState<number>(0)
    const [documentUrl, setDocumentUrl] = useState<string>('')
    const [videoUrl, setVideoUrl] = useState<string>('')
    const [videoCaptions, setVideoCaptions] = useState<Array<{ srclang: string; label: string; src: string }>>([])
    const [isModuleCompleted, setIsModuleCompleted] = useState<boolean>(false)
    const [completedModules, setCompletedModules] = useState<number[]>([])
    const [hasViewedContent, setHasViewedContent] = useState<boolean>(false)
    const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState<boolean>(false)

    const currentModuleIndex = parseInt(moduleIndex || '0', 10)
    const hasPrevious = currentModuleIndex > 0
    const hasNext = currentModuleIndex < totalModules - 1

    const handleContentCompleted = () => {
        setHasViewedContent(true)
        if (!isModuleCompleted) {
            if (module.questions && module.questions.length > 0) {
                setIsQuestionsModalOpen(true)
            } else {
                handleToggleCompletion()
            }
        }
    }

    useEffect(() => {
        const fetchModule = async () => {
            if (!trainingId || !moduleIndex) {
                setError(meta.errorInvalidParameters)
                setLoading(false)
                return
            }

            setLoading(true)
            setError(null)

            try {
                const volunteerId = adminMode ? null : user?.volunteer_csid

                console.log('Fetching module data for trainingId:', trainingId, 'moduleIndex:', moduleIndex, 'volunteerId:', volunteerId, 'adminMode:', adminMode)

                // Fetch training data - use volunteer endpoint if we have a volunteer ID to get completion data
                const endpoint = volunteerId
                    ? `${API_BASE_URL}/volunteer/${volunteerId}/training/${trainingId}`
                    : `${API_BASE_URL}/training/${trainingId}`

                const trainingResponse = await fetch(endpoint, { credentials: 'include' })
                if (!trainingResponse.ok) {
                    throw new Error(meta.errorTrainingNotFound)
                }

                const trainingResult = await trainingResponse.json()
                let training = trainingResult.value || trainingResult

                // Handle double-nested array
                if (Array.isArray(training) && training.length > 0 && Array.isArray(training[0])) {
                    training = training[0]
                }

                setTrainingName(training.name)
                setIsPolicy(training.policy || false)

                // Set completed modules array
                const completedMods = training.completed_modules || []
                setCompletedModules(completedMods)
                setIsModuleCompleted(completedMods.includes(currentModuleIndex))

                // Parse content
                let content = training.content
                if (typeof content === 'string') {
                    try {
                        content = JSON.parse(content)
                    } catch (e) {
                        throw new Error(meta.errorInvalidContent)
                    }
                }

                const modules = content?.modules || []
                setTotalModules(modules.length)
                const index = parseInt(moduleIndex, 10)

                if (index < 0 || index >= modules.length) {
                    throw new Error(meta.errorModuleNotFound)
                }

                const currentModule = modules[index]
                setModule(currentModule)



                // Handle document type modules
                if (currentModule.type === 'document') {
                    if (currentModule.file_key) {
                        const fileResponse = await fetch(`${API_BASE_URL}/file/${encodeURIComponent(currentModule.file_key)}`, { credentials: 'include' })
                        if (!fileResponse.ok) {
                            throw new Error(meta.errorFileNotFound)
                        }
                        const fileResult = await fileResponse.json()

                        if (!fileResult.success || !fileResult.data?.url) {
                            throw new Error(meta.errorNoDocumentSource)
                        }
                        setDocumentUrl(fileResult.data.url + '#toolbar=0&navpanes=0')
                    } else if (currentModule.url) {
                        setDocumentUrl(currentModule.url)
                    } else {
                        throw new Error(meta.errorNoDocumentSource)
                    }
                }

                // Handle video type modules
                if (currentModule.type === 'video') {
                    if (currentModule.file_key) {
                        // Check if it's a bunny.net video
                        if (currentModule.file_key.startsWith('bunny:')) {
                            const videoId = currentModule.file_key.substring(6)
                            const videoResponse = await fetch(`${API_BASE_URL}/video/${videoId}/playback`, { credentials: 'include' })
                            if (!videoResponse.ok) {
                                throw new Error(meta.errorFileNotFound)
                            }
                            const videoResult = await videoResponse.json()
                            if (!videoResult.success || !videoResult.data?.url) {
                                throw new Error(meta.errorNoVideoSource)
                            }
                            setVideoUrl(videoResult.data.url)
                            // Set captions if available
                            if (videoResult.data.captions && Array.isArray(videoResult.data.captions)) {
                                setVideoCaptions(videoResult.data.captions)
                            } else {
                                setVideoCaptions([])
                            }
                        } else {
                            // Regular file storage
                            const fileResponse = await fetch(`${API_BASE_URL}/file/${encodeURIComponent(currentModule.file_key)}`, { credentials: 'include' })
                            if (!fileResponse.ok) {
                                throw new Error(meta.errorFileNotFound)
                            }
                            const fileResult = await fileResponse.json()

                            if (!fileResult.success || !fileResult.data?.url) {
                                throw new Error(meta.errorNoVideoSource)
                            }
                            setVideoUrl(fileResult.data.url)
                            setVideoCaptions([]) // No captions for regular file storage
                        }
                    } else if (currentModule.url) {
                        setVideoUrl(currentModule.url)
                        setVideoCaptions([]) // No captions for URL-based videos
                    } else {
                        throw new Error(meta.errorNoVideoSource)
                    }
                }

            } catch (err) {
                setError(err instanceof Error ? err.message : meta.errorGeneric)
            } finally {
                setLoading(false)
            }
        }

        fetchModule()
    }, [trainingId, moduleIndex, meta, adminMode, user])

    // Reset content viewed state when module changes
    useEffect(() => {
        setHasViewedContent(false)
    }, [moduleIndex])

    const defaultBackUrl = adminMode ? `/trainings/${trainingId}` : `/volunteer/training/${trainingId}/details`

    const getModuleUrl = (moduleIndex: number) => {
        return adminMode
            ? `/trainings/${trainingId}/module/${moduleIndex}`
            : `/volunteer/training/${trainingId}/module/${moduleIndex}`
    }

    const handlePreviousModule = () => {
        if (hasPrevious) {
            navigate(getModuleUrl(currentModuleIndex - 1))
        }
    }

    const handleNextModule = () => {
        if (hasNext) {
            navigate(getModuleUrl(currentModuleIndex + 1))
        }
    }

    const handleToggleCompletion = async () => {
        if (!user?.volunteer_csid || !trainingId) return

        setTogglingCompletion(true)
        try {
            const newCompletionStatus = !isModuleCompleted

            const response = await fetch(
                `${API_BASE_URL}/training/${trainingId}/volunteer/${user.volunteer_csid}/module/${currentModuleIndex}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ completed: newCompletionStatus }),
                }
            )

            if (!response.ok) {
                throw new Error('Failed to update module completion status')
            }

            setIsModuleCompleted(newCompletionStatus)

            // Update the completed modules array
            if (newCompletionStatus) {
                const newCompletedModules = [...completedModules, currentModuleIndex]
                setCompletedModules(newCompletedModules)

                // Check if this was the last module and if this is a policy
                if (isPolicy && newCompletedModules.length === totalModules) {
                    // All modules complete - refresh incomplete policies count
                    await refreshIncompletePolicies()
                }
            } else {
                const wasCompleted = completedModules.length === totalModules
                setCompletedModules(completedModules.filter(idx => idx !== currentModuleIndex))

                // If unmarking a module in a policy, refresh count (training becomes incomplete)
                if (isPolicy && wasCompleted) {
                    await refreshIncompletePolicies()
                }
            }
        } catch (err) {
            console.error('Error toggling module completion:', err)
            // Optionally show error to user
        } finally {
            setTogglingCompletion(false)
        }
    }

    const renderModuleViewer = () => {
        if (!module) return null

        switch (module.type) {
            case 'document':
                if (!documentUrl) {
                    return (
                        <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-6 text-center">
                            <h3 className="text-lg font-semibold text-danger-900 dark:text-danger-200 mb-2">
                                {meta.errorTitle}
                            </h3>
                            <p className="text-danger-700 dark:text-danger-300">{meta.errorNoDocumentSource}</p>
                        </div>
                    )
                }
                return (
                    <DocumentViewer
                        fileUrl={documentUrl}
                        title={module.title}
                        meta={TrainingModulePageMeta.documentViewer}
                        onContentCompleted={handleContentCompleted}
                    />
                )

            case 'video':
                if (!videoUrl) {
                    return (
                        <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-6 text-center">
                            <h3 className="text-lg font-semibold text-danger-900 dark:text-danger-200 mb-2">
                                {meta.errorTitle}
                            </h3>
                            <p className="text-danger-700 dark:text-danger-300">{meta.errorNoVideoSource}</p>
                        </div>
                    )
                }
                return (
                    <VideoPlayer
                        videoUrl={videoUrl}
                        meta={TrainingModulePageMeta.videoViewer}
                        onContentCompleted={handleContentCompleted}
                        captions={videoCaptions}
                    />
                )

            case 'external':
                return (
                    <div className="text-center">
                        <a
                            href={module.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg"
                        >
                            Go to External Resource <ExternalLinkIcon />
                        </a>
                    </div>
                )
            case 'audio':
            case 'quiz':
            case 'interactive':
            case 'coming_soon':
                return (
                    <Card>
                        <div className="p-8 text-center">
                            <ConstructionIcon className="w-12 h-12 mx-auto mb-4 text-primary-500" />
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                Module coming soon
                            </h3>
                        </div>
                    </Card>
                )
            case 'scorm':
                return (
                    <TrainingScormPlayer />
                )
            default:
                return (
                    <UnsupportedModuleViewer
                        moduleType={module.type}
                        meta={TrainingModulePageMeta.unsupportedViewer}
                    />
                )
        }
    }

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4 max-w-6xl flex flex-col">
                    <Link
                        to={searchParams.get('back') || defaultBackUrl}
                        className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline mb-6"
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                        {isPolicy ? 'Back to Policy Details' : meta.backButtonLabel}
                    </Link>

                    {adminMode && (
                        <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                            <p className="text-sm text-primary-700 dark:text-primary-300">
                                <strong>Admin View:</strong> You are viewing this module in admin mode. Completion tracking is not available.
                            </p>
                        </div>
                    )}

                    {loading && <LoadingSpinner meta={LoadingSpinnerMeta} />}

                    {!loading && error && (
                        <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-6 text-center">
                            <h3 className="text-lg font-semibold text-danger-900 dark:text-danger-200 mb-2">
                                {meta.errorTitle}
                            </h3>
                            <p className="text-danger-700 dark:text-danger-300">{error}</p>
                        </div>
                    )}

                    {!loading && !error && module && (
                        <>


                            {/* Compact header with training info and module title */}
                            <div className="mb-4">
                                {/* Training breadcrumb */}
                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    <span>{trainingName}</span>
                                    {totalModules > 1 && (
                                        <>
                                            <span>•</span>
                                            <span>{meta.moduleLabel.replace('{current}', String(parseInt(moduleIndex || '0') + 1)).replace('{total}', String(totalModules))}</span>
                                        </>
                                    )}
                                </div>

                                {/* Module title and description */}
                                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                    {module.title}
                                </h1>
                                {module.description && (
                                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                                        {module.description}
                                    </p>
                                )}
                            </div>

                            {/* Navigation and completion controls */}
                            {module.type !== 'coming_soon' && (<div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    {/* Navigation buttons */}
                                    {totalModules > 1 ? (
                                        <div className="flex items-center gap-3">
                                            <Button
                                                onClick={handlePreviousModule}
                                                disabled={!hasPrevious}
                                                variant="outline"
                                                size="sm"
                                                className="flex items-center gap-2"
                                            >
                                                <ChevronLeftIcon className="w-4 h-4" />
                                                {meta.previousModuleButton}
                                            </Button>

                                            <Button
                                                onClick={handleNextModule}
                                                disabled={!hasNext}
                                                variant="outline"
                                                size="sm"
                                                className="flex items-center gap-2"
                                            >
                                                {meta.nextModuleButton}
                                                <ChevronRightIcon className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}

                                    {/* Mark as Complete button or Preview Questions button */}
                                    {!adminMode && user?.volunteer_csid && (
                                        <div className="flex items-center gap-3">
                                            {isModuleCompleted ? (
                                                <Button
                                                    onClick={handleToggleCompletion}
                                                    variant="outline"
                                                    color="neutral"
                                                    size="sm"
                                                    disabled={togglingCompletion}
                                                >
                                                    {togglingCompletion ? 'Updating...' : <><XIcon /> Mark as Incomplete</>}
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        onClick={() => {
                                                            if (module.questions && module.questions.length > 0) {
                                                                setIsQuestionsModalOpen(true)
                                                            } else {
                                                                handleToggleCompletion()
                                                            }
                                                        }}
                                                        variant="solid"
                                                        color="success"
                                                        size="sm"
                                                        disabled={!hasViewedContent || togglingCompletion}
                                                    >
                                                        {togglingCompletion ? 'Updating...' : <><CheckIcon /> {isPolicy ? 'Acknowledge Section' : 'Complete Module'}</>}
                                                    </Button>
                                                    {!hasViewedContent && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                                            {module?.type === 'video' ? `Watch the video to enable ${isPolicy ? 'acknowledgment' : 'completion'}` : `Review the document to enable ${isPolicy ? 'acknowledgment' : 'completion'}`}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {adminMode && module?.questions && module.questions.length > 0 && (
                                        <Button
                                            onClick={() => setIsQuestionsModalOpen(true)}
                                            variant="outline"
                                            color="primary"
                                            size="sm"
                                        >
                                            Preview Questions ({module.questions.length})
                                        </Button>
                                    )}
                                </div>
                            </div>)}

                            {/* Module viewer */}
                            {renderModuleViewer()}
                        </>
                    )}
                </div>
            </div>


            <CompletionQuestionsModal
                meta={ModuleCompletionModalMeta}
                questions={module?.questions || []}
                isOpen={isQuestionsModalOpen}
                onClose={() => setIsQuestionsModalOpen(false)}
                onComplete={() => {
                    // Update local state without API call (API already called by modal)
                    setIsModuleCompleted(true)
                    const newCompletedModules = [...completedModules, currentModuleIndex]
                    setCompletedModules(newCompletedModules)
                    // Check if this was the last module and if this is a policy
                    if (isPolicy && newCompletedModules.length === totalModules) {
                        refreshIncompletePolicies()
                    }
                }}
                previewMode={adminMode}
                trainingId={trainingId}
                volunteerId={user?.volunteer_csid}
                moduleIndex={currentModuleIndex}
            />
        </Fragment>
    )
}
